/**
 * The customer map's site browser — the searchable list that mirrors the
 * markers on the map.
 *
 * Everything on the map is also a row here, so a site that is clustered,
 * off-screen or simply hard to spot is still reachable: hovering a row lifts
 * its marker, the locate action flies the map to it, and clicking opens the
 * site. The list virtualizes because a large customer has thousands of sites.
 */
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoSizer, List } from 'react-virtualized';

import { deriveUnitStatus, hasMaintenance } from '@sophocles/features/map/mapState';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';
import MapSidebarHeader from '@sophocles/views/map/components/MapSidebarHeader';
import MapSiteListRow from '@sophocles/views/map/components/MapSiteListRow';
import { CONFIGURABLE_FIELDS, fieldLabel } from '@sophocles/views/map/mapFields';

// A row is a fixed stack of single, ellipsised lines, so its height is
// arithmetic rather than a measurement: the name line always, then the site's
// description, one line per configured detail field, and the badge line
// carrying alarm chips, maintenance and the group name. A row that carries
// none of the optional lines is genuinely shorter, which is why the virtualized
// list sizes each row rather than taking one height for all of them.
const ROW_NAME_HEIGHT = 46;
const ROW_TEXT_LINE_HEIGHT = 20;
const ROW_BADGE_LINE_HEIGHT = 28;
const OVERSCAN_ROWS = 8;

/**
 * @param {object} props
 * @param {object[]} props.rows - the sites to render.
 * @param {number} props.totalCount - rows before filtering, for the header count.
 * @param {string} props.title - what the rows are, in the customer's vocabulary.
 * @param {{ field: string, direction: string }} props.sort
 * @param {(sort: { field: string, direction: string }) => void} props.onSortChange
 * @param {?number} props.hoveredSiteId
 * @param {?number} props.selectedSiteId
 * @param {(siteId: ?number) => void} props.onHoverSite
 * @param {(item: object) => void} props.onSelectSite
 * @param {(item: object) => void} props.onFindOnMap
 * @param {string[]} props.shownFields
 * @param {(fields: string[]) => void} props.onShownFieldsChange
 * @param {boolean} [props.showGroupName] - rows name their group (off when the map is filtered to one).
 */
export default function MapSidebar({
  rows,
  totalCount,
  title,
  sort,
  onSortChange,
  hoveredSiteId,
  selectedSiteId,
  onHoverSite,
  onSelectSite,
  onFindOnMap,
  shownFields,
  onShownFieldsChange,
  showGroupName = false,
}) {
  const { t } = useTranslation();
  const relayLoadTerm = useRelayLoadTerm();

  const labelsById = useMemo(() => {
    const relayLoadTermPlural = relayLoadTerm.plural;
    return CONFIGURABLE_FIELDS.reduce(
      (labels, field) => ({ ...labels, [field.id]: fieldLabel(field, { t, relayLoadTermPlural }) }),
      {},
    );
  }, [t, relayLoadTerm.plural]);

  const rowHeight = useCallback(
    ({ index }) => {
      const site = rows[index];
      const textLines = (site.description ? 1 : 0) + shownFields.length;
      const hasBadges =
        (site.alarms?.length ?? 0) > 0 || hasMaintenance(site) || Boolean(showGroupName && site.site_group_name);
      return ROW_NAME_HEIGHT + textLines * ROW_TEXT_LINE_HEIGHT + (hasBadges ? ROW_BADGE_LINE_HEIGHT : 0);
    },
    [rows, shownFields, showGroupName],
  );

  // The list caches every row's offset, so a new set of rows or a change to
  // what a row carries has to invalidate that cache by hand — otherwise the
  // rows render at the previous set's heights and drift apart as you scroll.
  const listRef = useRef(null);
  useEffect(() => listRef.current?.recomputeRowHeights(), [rowHeight]);

  const rowRenderer = ({ index, key, style }) => {
    const item = rows[index];
    const siteId = item.site_id ?? item.id;
    return (
      <MapSiteListRow
        key={key}
        style={style}
        item={item}
        status={deriveUnitStatus(item)}
        isHovered={hoveredSiteId != null && siteId === hoveredSiteId}
        isSelected={selectedSiteId != null && siteId === selectedSiteId}
        onHover={onHoverSite}
        onSelect={onSelectSite}
        onFind={onFindOnMap}
        shownFields={shownFields}
        labelsById={labelsById}
        showGroupName={showGroupName}
      />
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      <MapSidebarHeader
        title={title}
        visibleCount={rows.length}
        totalCount={totalCount}
        sort={sort}
        onSortChange={onSortChange}
        shownFields={shownFields}
        onShownFieldsChange={onShownFieldsChange}
      />

      {rows.length === 0 ? (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('map.noDataCheckFilters', 'No data found — check your filters')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <AutoSizer>
            {({ width, height }) => (
              <List
                ref={listRef}
                width={width}
                height={height}
                rowCount={rows.length}
                rowHeight={rowHeight}
                rowRenderer={rowRenderer}
                overscanRowCount={OVERSCAN_ROWS}
              />
            )}
          </AutoSizer>
        </Box>
      )}
    </Box>
  );
}
